// Pure geometry + population logic for the single-player Stats renderer (04h-2), ported
// from js/renderers-stats.js's window.renderStatsModule. Kept pure (population passed in,
// no DOM) so the percentile-bar math and the compare-against subset are unit-tested — the
// StatsModule component is a thin shell over these. The tuned filter engine itself lives in
// src/engines/stats-filter-kit.ts; this is the Stats-specific glue (fullRange /
// computeFilteredPopulation / the marker + range fractions) the prototype kept inline.

import {
  matchesFilters,
  metricValueForMode,
  hasTotalMode,
  RANGE_FILTERS,
  type ActiveFilters,
  type KitPlayer,
  type KitMetric,
  type RangeFilterConfig,
} from "../engines/stats-filter-kit.js";

export interface Range {
  min: number;
  max: number;
}

/** Clamp a fraction to 0.01–0.99 so a marker never sits flush on either edge of its
 *  track (renderers-stats.js:103 — 0 is a normal, reachable position for a range handle,
 *  but a marker pinned to the very edge reads as clipped). */
export function clamp01(v: number): number {
  return Math.max(0.01, Math.min(0.99, v));
}

/**
 * The compare-against population: every player who passes all active filters, relative to
 * the reference player for the player-relative range filters (renderers-stats.js:291). The
 * population is passed in (pure/data-injected, like the engine) rather than read off window.
 */
export function computeFilteredPopulation(
  population: readonly KitPlayer[],
  active: ActiveFilters,
  refPlayer: KitPlayer,
  rangeFilters: RangeFilterConfig = RANGE_FILTERS,
): KitPlayer[] {
  return population.filter((pl) => matchesFilters(pl, active, refPlayer, rangeFilters));
}

// Collect the real (non-null, non-NaN) values for a metric across a set of players on the
// active value scale. Reads each player's TOP-LEVEL current-season stats map (pl.stats) —
// exactly what the prototype's fullRange/filtered-range scan, NOT the selected-season map:
// the population bar is a fixed all-players-current-season scale, independent of which
// season the reference player is being viewed in.
function extractValues(pop: readonly KitPlayer[], metric: KitMetric, mode: string): number[] {
  const out: number[] = [];
  for (const pl of pop) {
    const v = metricValueForMode(pl.stats ?? {}, metric, mode);
    if (v !== undefined && v !== null && !Number.isNaN(v)) out.push(v);
  }
  return out;
}

/** The full, unfiltered min/max for a metric across the whole population on the active
 *  scale (per90 or total). {min:0,max:0} when nobody has a value (renderers-stats.js:180). */
export function fullRange(population: readonly KitPlayer[], metric: KitMetric, mode: string): Range {
  const values = extractValues(population, metric, mode);
  return values.length ? { min: Math.min(...values), max: Math.max(...values) } : { min: 0, max: 0 };
}

/** The real recorded min/max over the FILTERED subset on the same scale, or null when the
 *  subset records no value for this metric (renderers-stats.js:1002). */
export function filteredExtent(filtered: readonly KitPlayer[], metric: KitMetric, mode: string): Range | null {
  const values = extractValues(filtered, metric, mode);
  if (!values.length) return null;
  return { min: Math.min(...values), max: Math.max(...values) };
}

/** Marker position (fraction, clamped to 0.01–0.99) for `current` on the full-population
 *  scale. A degenerate scale (max ≤ min) sits the marker mid-track (renderers-stats.js:992). */
export function markerFraction(current: number, full: Range): number {
  const pct = full.max > full.min ? (current - full.min) / (full.max - full.min) : 0.5;
  return clamp01(pct);
}

/**
 * The filtered-subset range bar mapped onto the full scale: {left, width} as 0–1 fractions,
 * or null when there's nothing drawable — no subset values, or a degenerate full scale
 * (renderers-stats.js:1005). Endpoints are clamped to [0,1]; the bar moves independently of
 * the marker (a filtered subgroup's real range needn't contain the reference player).
 */
export function rangeBarFraction(extent: Range | null, full: Range): { left: number; width: number } | null {
  if (!extent || full.max <= full.min) return null;
  const span = full.max - full.min;
  const lo = Math.max(0, Math.min(1, (extent.min - full.min) / span));
  const hi = Math.max(0, Math.min(1, (extent.max - full.min) / span));
  return { left: lo, width: Math.max(hi - lo, 0) };
}

/** Does this category have at least one per-90 metric? Only then does the Per 90/Total
 *  toggle mean anything (Appearance is all real counts) — renderers-stats.js:824. */
export function categoryHasTotalMode(category: {
  subcategories: { playerMetrics: KitMetric[] }[];
}): boolean {
  return category.subcategories.some((sub) => sub.playerMetrics.some((m) => hasTotalMode(m)));
}

/** A single-subcategory category whose one group repeats the category name shows no group
 *  label (it would just echo the module title) — renderers-stats.js:915. */
export function shouldSkipGroupLabel(category: {
  name: string;
  subcategories: { name: string }[];
}): boolean {
  return category.subcategories.length === 1 && category.subcategories[0]!.name === category.name;
}

/** How a value ranks in a group (Ben, 2026-10-18: "better than 82% of Premier League
 *  midfielders"): the share of the group it beats — strictly below it, or above it for a
 *  lower-is-better stat — as whole per cent rounded down. Null with fewer than 2 in the group. Pure. */
export function percentBetter(value: number, sorted: readonly number[], lowerIsBetter: boolean): number | null {
  if (sorted.length < 2) return null;
  let beaten = 0;
  for (const v of sorted) if (lowerIsBetter ? v > value : v < value) beaten++;
  return Math.floor((beaten / sorted.length) * 100);
}

/** The bar's hover line for it, in percentile terms (Ben, 2026-10-18: as Catalyst and our Bio's
 *  HOPS): "36th percentile · 412 Premier League players compared". A lower-is-better stat is turned
 *  round (a high percentile = good) and says so. */
export function percentBetterText(pct: number, count: number, who: string, lowerIsBetter = false): string {
  return `${ordinalOf(pct)} percentile${lowerIsBetter ? " (lower is better)" : ""} · ${count.toLocaleString()} ${who}`;
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th", 92 → "92nd". */
function ordinalOf(n: number): string {
  const t = n % 100;
  const last = n % 10;
  return `${n}${t >= 11 && t <= 13 ? "th" : last === 1 ? "st" : last === 2 ? "nd" : last === 3 ? "rd" : "th"}`;
}
